import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useIsMounted from '../../hooks/useIsMounted';
import ConfirmButton from './ConfirmButton';
import BackendHandlingButton from './BackendHandlingButton';
import { extendedMutationType } from '../../types';
import Spinner from '../Spinner';
import './_styles_/MutationButton.css';
import {
    isExtraBackendConfirmationNeeded,
    isFourEyesPrincipleNeeded,
} from '../errors/MiscErrors';
import VariablesProvidingButton from './VariablesProvidingButton';

MutationButton.propTypes = {
    children: PropTypes.node.isRequired,
    extendedMutation: extendedMutationType.isRequired,
    confirmationMessage: PropTypes.string.isRequired,
    extraConfirmationMessage: PropTypes.string,
    showConfirmationDialog: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    variablesComp: PropTypes.func,
};

MutationButton.defaultProps = {
    refetchQueries: [],
    className: '',
    disabled: false,
    showConfirmationDialog: false,
    loading: false,
    extraConfirmationMessage: null,
    variablesComp: null,
};

export const isBackendConfirmationError = (error) => isFourEyesPrincipleNeeded(error) || isExtraBackendConfirmationNeeded(error);

function MutationButton(props) { // NOSONAR javascript:S3776
    const {
        children,
        className,
        disabled,
        confirmationMessage,
        extraConfirmationMessage,
        showConfirmationDialog,
        extendedMutation,
        variablesComp,
    } = props;
    const { t } = useTranslation();
    const [isConfirmed, setConfirmed] = useState(false);
    const [isVariablesSet, setVariablesSet] = useState(false);
    const [variables, setVariables] = useState(null);
    const isMounted = useIsMounted();
    const onVariablesProvidingButtonHandler = async (newVariables) => {
        setVariablesSet(true);
        setVariables(newVariables);
        const mutationResult = await extendedMutation.mutate(newVariables);
        if (isMounted() && mutationResult && !isBackendConfirmationError(mutationResult.errors)) {
            setConfirmed(false);
            setVariablesSet(false);
        }
    };
    const onConfirmButtonHandler = async () => {
        setConfirmed(true);
        if (!variablesComp) {
            const mutationResult = await extendedMutation.mutate();
            if (isMounted() && mutationResult && !isBackendConfirmationError(mutationResult.errors)) {
                setConfirmed(false);
                setVariablesSet(false);
            }
        }
    };
    const onCancelHandler = () => {
        setConfirmed(false);
        setVariablesSet(false);
        extendedMutation.reset();
    };
    const onBackendHandlingConfirmHandler = async (extraVariables) => {
        const mutationResult = await extendedMutation.mutate({ ...variables, ...extraVariables });
        if (isMounted() && mutationResult && !isBackendConfirmationError(mutationResult.errors)) {
            setConfirmed(false);
            setVariablesSet(false);
        }
    };
    const showConfirmButton = showConfirmationDialog && !isConfirmed;
    const wasConfirmButtonConfirmed = showConfirmationDialog && isConfirmed;
    const showVariablesProvidingButton = variablesComp && !isVariablesSet;
    let result;
    if (extendedMutation.loading && !extendedMutation.isFourEyesNeeded) {
        result = (
            <button type="button" disabled className={className}>
                <Spinner size={10} displayLabel={false} className="mutation-button-loading" />
                {children}
            </button>
        );
    } else if (showConfirmButton) {
        result = (
            <ConfirmButton
                className={className}
                message={confirmationMessage}
                onConfirm={onConfirmButtonHandler}
                onCancel={onCancelHandler}
                disabled={disabled}
            >
                {children}
            </ConfirmButton>
        );
    } else if (showVariablesProvidingButton) {
        result = (
            <VariablesProvidingButton
                className={className}
                message={confirmationMessage}
                onConfirm={onVariablesProvidingButtonHandler}
                onCancel={onCancelHandler}
                disabled={disabled}
                variablesProvidingComp={variablesComp}
                showPopupOnLoad={wasConfirmButtonConfirmed}
            >
                {children}
            </VariablesProvidingButton>
        );
    } else {
        result = (
            <BackendHandlingButton
                extendedMutation={extendedMutation}
                className={className}
                message={confirmationMessage}
                extraConfirmationMessage={extraConfirmationMessage || t('misc.default-extra-confirmation-message')}
                disabled={disabled}
                onConfirm={onBackendHandlingConfirmHandler}
                onCancel={onCancelHandler}
            >
                {children}
            </BackendHandlingButton>
        );
    }
    return result;
}

export default MutationButton;
